import { doGET } from "./HttpUtil";

const parseSegment = (segment) => {
  const match = segment.match(/^(.+?)(\[(\d+)?\])?$/);
  if (!match) return { key: segment, isArray: false, index: null };
  return {
    key: match[1],
    isArray: !!match[2],
    index: match[3] !== undefined ? Number(match[3]) : null,
  };
};

/**
 * Safely resolve values from nested data using dotted paths with array support.
 * Supports:
 * - individuals[].role (iterates all)
 * - individuals[0].role (specific index)
 * - address.country (direct)
 */
const getValuesByPath = (data, path) => {
  if (!path || data === undefined || data === null) return [];

  const segments = path.split(".");

  const traverse = (node, idx) => {
    if (node === undefined || node === null) return [];
    if (idx >= segments.length) return [node];

    const { key, isArray, index } = parseSegment(segments[idx]);
    const next = node?.[key];

    if (isArray) {
      if (Array.isArray(next)) {
        if (index !== null && next[index] !== undefined) {
          return traverse(next[index], idx + 1);
        }
        return next.flatMap((item) => traverse(item, idx + 1));
      }
      return traverse(next, idx + 1);
    }

    return traverse(next, idx + 1);
  };

  return traverse(data, 0).filter((v) => v !== undefined);
};

const getBindingInfo = (fieldName = "") => {
  const segments = fieldName.split(".").filter(Boolean);
  let lastWildcardIndex = -1;

  segments.forEach((segment, idx) => {
    const { isArray, index } = parseSegment(segment);
    if (isArray && index === null) {
      lastWildcardIndex = idx;
    }
  });

  if (lastWildcardIndex === -1) {
    return {
      bindingPath: null,
      relativePath: fieldName,
    };
  }

  return {
    bindingPath: segments.slice(0, lastWildcardIndex + 1).join("."),
    relativePath: segments.slice(lastWildcardIndex + 1).join("."),
  };
};

const parseComparatorValues = (values) => {
  if (values === undefined || values === null) return [];
  if (Array.isArray(values)) return values;
  return String(values)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
};

const normalizeValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value;

  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && String(value).trim() !== "") {
    return asNumber;
  }

  const lower = String(value).trim().toLowerCase();
  if (lower === "true") return true;
  if (lower === "false") return false;
  return lower;
};

const compareValue = (operator, sourceValue, comparatorRaw) => {
  const comparatorValues = parseComparatorValues(comparatorRaw).map(normalizeValue);
  const normalizedSource = normalizeValue(sourceValue);

  const inList = comparatorValues.some((val) => val === normalizedSource);

  switch (operator) {
    case "EQ":
      return comparatorValues.length ? inList : normalizedSource === normalizeValue(comparatorRaw);
    case "NE":
      return comparatorValues.length ? !inList : normalizedSource !== normalizeValue(comparatorRaw);
    case "IN":
      return inList;
    case "NOT_IN":
      return !inList;
    case "GT":
      return typeof normalizedSource === "number" && normalizedSource > comparatorValues[0];
    case "GTE":
      return typeof normalizedSource === "number" && normalizedSource >= comparatorValues[0];
    case "LT":
      return typeof normalizedSource === "number" && normalizedSource < comparatorValues[0];
    case "LTE":
      return typeof normalizedSource === "number" && normalizedSource <= comparatorValues[0];
    default:
      return false;
  }
};

const getConditionValues = (condition = {}, applicationData = {}, bindings = {}) => {
  if (!condition?.fieldName) return [];
  const { bindingPath, relativePath } = getBindingInfo(condition.fieldName);

  if (!bindingPath) {
    return getValuesByPath(applicationData, condition.fieldName);
  }

  if (!(bindingPath in bindings)) {
    return [];
  }

  const boundNode = bindings[bindingPath];
  if (boundNode === undefined || boundNode === null) {
    return [];
  }

  if (!relativePath) {
    return [boundNode];
  }

  return getValuesByPath(boundNode, relativePath);
};

const evaluateCondition = (condition = {}, applicationData = {}, bindings = {}) => {
  if (!condition?.fieldName) return false;
  const values = getConditionValues(condition, applicationData, bindings);
  if (!values.length) return false;
  const operator = condition?.operator || "EQ";
  return values.some((val) => compareValue(operator, val, condition?.values));
};

const getRuleBindingPaths = (rule = {}) =>
  Array.from(
    new Set(
      [...(Array.isArray(rule?.andConditions) ? rule.andConditions : []), ...(Array.isArray(rule?.orConditions) ? rule.orConditions : [])]
        .map((condition) => getBindingInfo(condition?.fieldName).bindingPath)
        .filter(Boolean),
    ),
  );

const evaluateRuleWithBindings = (rule = {}, applicationData = {}, bindings = {}) => {
  const andList = Array.isArray(rule?.andConditions) ? rule.andConditions : [];
  const orList = Array.isArray(rule?.orConditions) ? rule.orConditions : [];

  const orPass = orList.length ? orList.some((c) => evaluateCondition(c, applicationData, bindings)) : true;
  const andPass = andList.length ? andList.every((c) => evaluateCondition(c, applicationData, bindings)) : true;

  return orPass && andPass;
};

const ruleMatches = (rule, applicationData) => {
  if (rule?.status !== 1) return false;

  const bindingPaths = getRuleBindingPaths(rule);
  if (!bindingPaths.length) {
    return evaluateRuleWithBindings(rule, applicationData);
  }

  const bindingCandidates = bindingPaths.reduce((acc, path) => {
    const candidates = getValuesByPath(applicationData, path);
    acc[path] = candidates.length ? candidates : [undefined];
    return acc;
  }, {});

  const searchBindings = (idx, bindings) => {
    if (idx >= bindingPaths.length) {
      return evaluateRuleWithBindings(rule, applicationData, bindings);
    }

    const currentPath = bindingPaths[idx];
    return bindingCandidates[currentPath].some((candidate) =>
      searchBindings(idx + 1, { ...bindings, [currentPath]: candidate }),
    );
  };

  return searchBindings(0, {});
};

const extractDocTypes = (rules, applicationData) =>
  Array.from(
    new Set(
      (rules || [])
        .filter((rule) => ruleMatches(rule, applicationData))
        .flatMap((rule) => rule?.docTypes || [])
        .filter(Boolean),
    ),
  );

const ruleCache = new Map();

export const fetchDocTypeRules = async (formType, { force = false } = {}) => {
  if (!formType) return [];
  if (!force && ruleCache.has(formType)) {
    return ruleCache.get(formType);
  }

  try {
    const response = await doGET(`/api/doc-type-rule/grid?rows=-1&formType=${formType}`);
    const rules = response?.data?.rows ?? [];
    ruleCache.set(formType, rules);
    return rules;
  } catch (error) {
    return [];
  }
};

export const evaluateDocTypeRules = (rules = [], applicationData = {}) => {
  if (!Array.isArray(rules) || !rules.length) return [];
  try {
    return extractDocTypes(rules, applicationData);
  } catch (error) {
    return [];
  }
};

/**
 * Calculate unique document type keys required for a given form type
 * based on Doc Type Rules evaluated against application data.
 */
export const calculateListofDocumentTypes = async ({ formType, applicationData, force = false }) => {
  if (!formType) return [];
  const rules = await fetchDocTypeRules(formType, { force });
  return extractDocTypes(rules, applicationData);
};

export const formatIndividualName = (individual = {}) => [individual?.firstName, individual?.middleName, individual?.lastName].filter(Boolean).join(' ').trim();

export default calculateListofDocumentTypes;
